import React, {useContext, useState} from 'react';
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Input from "@amzn/meridian/input";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import {FormattedMessage, injectIntl} from "react-intl";
import Column from "@meridian/components/column";
import Text from "@amzn/meridian/text";
import Alert from "@amzn/meridian/alert"
import Select, {SelectOption} from "@amzn/meridian/select";
import {
    checkModalValidationsForTestPackage, findConflictingBagTier, isStackedStowTestApplicable,
    isValidEntry, PATTERNS
} from "../../../util/Validations";
import {DraftContext} from "../../../draftSave/context/DraftSaveContext";
import {CONFIG_TYPE, STOW_STATION_TIERS} from "../../../util/Constants";
import {getTestConfigKey} from "../../../util/Util";

const AddAdtfSorterTestPackageModal = (props) => {

    const {formatMessage} = props.intl;
    const {mac} = props;
    const {getDraftData, addInstance, editInstance} = useContext(DraftContext);

    // Visual controls
    const {open, setOpen} = props;
    const [show, setShow] = useState(false);
    const [edit, setEdit] = useState(false);
    const [conflict, setConflict] = useState(null);

    // Values
    const [scannables, setScannables] = useState("");
    const [sortZone, setSortZone] = useState("");
    const [bag, setBag] = useState("");
    const [tier, setTier] = useState("");

    // Error
    const [scannablesErr, setScannablesErr] = useState(false);
    const [sortZoneErr, setSortZoneErr] = useState(false);

    const testPackageRows = getDraftData({stateKey: CONFIG_TYPE.ADTF_SORTER_TEST_PACKAGE, subKey: mac}) || [];

    // Decided once for this device; the stacked stow fields either exist here or they do not.
    const isStackedStowTestEnabled = isStackedStowTestApplicable(
        getDraftData({stateKey: CONFIG_TYPE.ADTF_SORTER_DESTINATION, subKey: mac}));

    function onCloseModal() {
        setShow(false);
        setOpen(false);
        resetInputState();
    }

    /**
     * Bag and tier are optional and independent, so empty values are left off the row entirely
     * rather than written as empty strings. That matters for edits: an untouched legacy row then
     * compares equal to its persisted form, instead of being marked as changed because "" and
     * undefined differ.
     */
    const buildSorterTestPackage = () => {
        const builtTestPackage = {
            scannables: scannables.split(';').filter(Boolean),
            sortZone: sortZone
        }
        if (!isStackedStowTestEnabled) return builtTestPackage;
        if (bag) builtTestPackage.bag = bag;
        if (tier) builtTestPackage.tier = tier;
        return builtTestPackage;
    }

    const sorterTestPackage = buildSorterTestPackage();

    const testConfigMap = {}
    testPackageRows.forEach((value) => {
        const key = getTestConfigKey(value.scannables);
        testConfigMap[key] = {
            sortZone: value.sortZone,
            bag: value.bag,
            tier: value.tier
        };
    })

    /**
     * Rows for other packages, i.e. everything the row being added or edited could collide with.
     */
    const otherTestPackageRows = () => {
        const key = getTestConfigKey(sorterTestPackage.scannables);
        return testPackageRows.filter((value) => getTestConfigKey(value.scannables) !== key);
    }

    const onClickConfirm = (sorterTestPackage, mac) => {
        if (!checkModalValidationsForTestPackage(sorterTestPackage)) {
            setShow(true);
            return;
        }
        const conflictingRow = isStackedStowTestEnabled
            ? findConflictingBagTier(otherTestPackageRows(), sorterTestPackage)
            : null;
        if (conflictingRow) {
            setConflict(conflictingRow);
            return;
        }
        setConfigData(sorterTestPackage, mac);
        setOpen(false);
        resetInputState();
        setShow(false);
    }

    const prefill = () => {
        const key = getTestConfigKey(sorterTestPackage.scannables);
        if (testConfigMap.hasOwnProperty(key)) {
            setEdit(true);
            resetConfigErrorDetails();
            setSortZone(testConfigMap[key].sortZone);
            // Coalesced because the fields are optional and the inputs must stay controlled
            setBag(testConfigMap[key].bag || "");
            setTier(testConfigMap[key].tier || "");
        } else if (edit) {
            setEdit(false);
            resetConfigDetails()
        }
    }

    function resetInputState() {
        setScannables("");
        resetConfigDetails();
        resetConfigErrorDetails();
    }

    const resetConfigDetails = () => {
        setSortZone("");
        resetStackedStowTestFields();
    }

    const resetStackedStowTestFields = () => {
        setBag("");
        setTier("");
        setConflict(null);
    }

    const resetConfigErrorDetails = () => {
        setSortZoneErr(false);
    }

    const setConfigData = (sorterTestPackage, mac) => {
        if(edit) {
            editInstance({
                stateKey: CONFIG_TYPE.ADTF_SORTER_TEST_PACKAGE,
                subKey: mac,
                instance: sorterTestPackage,
                keyComparator: (config1, config2) => (
                    getTestConfigKey(config1.scannables) === getTestConfigKey(config2.scannables)
                ),
                valueComparator: (config1, config2) => {
                    return config1.sortZone === config2.sortZone
                        && config1.bag === config2.bag
                        && config1.tier === config2.tier;
                }
            });
        } else {
            addInstance({
                stateKey: CONFIG_TYPE.ADTF_SORTER_TEST_PACKAGE,
                subKey: mac,
                instance: sorterTestPackage
            });
        }
    }

    return (
        <Row>
            <Modal
                title={formatMessage({id: "sortertestconfig.addupdatetest"})}
                open={open}
                onClose={onCloseModal}
                scrollContainer="viewport"
                closeLabel="Close"
                bodySpacingInset="medium">
                <Column spacing={"medium"} spacingInset={"medium"}>
                    <Column spacing="200">
                        <b> <FormattedMessage id="modal.addheadingtest"
                                              defaultMessage="To edit an existing configuration, enter its Sort Zone."/>
                        </b>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                            <FormattedMessage id="bay.scannables" defaultMessage="Scannables"/>
                        </Text>
                        <Input id="scannables-input"
                               value={scannables}
                               onChange={(value) => {setScannables(value.replace(/\s/g, ''))}}
                               onBlur={() => {
                                   setScannablesErr(!isValidEntry(scannables, PATTERNS.TEST_SCANNABLE));
                                   prefill();
                               }}
                               error={scannablesErr}
                               helperText={<FormattedMessage id="bay.scannables_description"
                                 defaultMessage="Tracking/Shipment ID. Separate multiple values by semicolon (;)"/>}/>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                            <FormattedMessage id="bay.sortzone"
                                              defaultMessage="Sort Zone"/>
                        </Text>
                        <Input id="sort-zone-input"
                               value={sortZone}
                               onChange={setSortZone}
                               onBlur={() => {setSortZoneErr(!isValidEntry(sortZone, PATTERNS.SORT_ZONE))}}
                               error={sortZoneErr}
                               helperText={<FormattedMessage id="bay.sortzone_description"
                                         defaultMessage="eg. EU: A1-1A, NA: A-1.1A"/> }/>
                    </Column>
                    {isStackedStowTestEnabled &&
                    <Column spacing={"medium"}>
                        <Column spacing="200">
                            <Text tag="label" type="b200" color="primary" htmlFor="bag-input">
                                <FormattedMessage id="bay.bag" defaultMessage="Bag"/>
                            </Text>
                            <Input id="bag-input"
                                   value={bag}
                                   onChange={(value) => {setBag(value.replace(/\s/g, '')); setConflict(null)}}
                                   helperText={<FormattedMessage id="bay.bag_description"
                                     defaultMessage="Optional. Bag the planning system assigns a tier to."/>}/>
                        </Column>
                        <Column spacing="200">
                            <Text tag="label" type="b200" color="primary" htmlFor="tier-input">
                                <FormattedMessage id="bay.tier" defaultMessage="Tier"/>
                            </Text>
                            <Select id="tier-input"
                                    value={tier}
                                    onChange={(value) => {setTier(value); setConflict(null)}}
                                    placeholder={formatMessage({id: "bay.tier_description",
                                        defaultMessage: "Optional. Stow station tier to divert to."})}
                                    size="medium">
                                {STOW_STATION_TIERS.map(({label, value}) =>
                                    <SelectOption key={value} value={value} label={label}/>
                                )}
                            </Select>
                        </Column>
                    </Column>}
                </Column>
                <ModalFooter>
                    <Column>
                        {!checkModalValidationsForTestPackage(sorterTestPackage) &&
                            show &&
                            <Alert type="warning" size="small">
                                <FormattedMessage id="alert.missingfields"
                                                  defaultMessage="Please enter all fields in correct format!"/>
                            </Alert>}
                        {conflict &&
                            <Alert type="warning" size="small">
                                <FormattedMessage id="alert.conflictingbagtier"
                                                  defaultMessage="Another test package already assigns this bag to a different tier. Two rows cannot disagree about the tier of one bag."/>
                            </Alert>}
                    </Column>
                    <Row alignmentHorizontal="right" widths="fit">
                        <Button type="secondary" size="medium" onClick={onCloseModal}>
                            <FormattedMessage id="modal.cancel" defaultMessage="Cancel"/>
                        </Button>
                        <Button type="primary" size="medium"
                                onClick={() => onClickConfirm(sorterTestPackage, mac)}>
                            { edit ? <FormattedMessage id="modal.confirmUpdate" defaultMessage="Confirm update"/>
                                : <FormattedMessage id="modal.confirmAdd" defaultMessage="Confirm add"/>}
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
        </Row>
    )
}

export default injectIntl(AddAdtfSorterTestPackageModal);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/adtfdevice/modal/AddAdtfSorterTestPackageModal.js